/**
 * Heat Loss Calculator: Configs
 * ===
 * Configuration values and data for the Stelrad Heat Loss Calculator
 * 
 *
 * Copyright (c) August 2024 · Art & Soul Communications Ltd.
 * https://artandsoul.co.uk
 *
 */

'use strict';

export var HLC_Configs = {
    
    radiator_button_anchor_text: undefined,
    radiator_button_title_text: undefined,
    
    hlc_version: undefined,
    
    hlc_version_configs: {
        homeowner : {
            radiator_button_anchor_text: 'Buy now',
            radiator_button_title_text: 'Buy online direct from Stelrad'
        },
        social : {
            radiator_button_anchor_text: 'See info',
            radiator_button_title_text: 'View details on the Stelrad website'
        },
        commercial : {
            radiator_button_anchor_text: 'See info',
            radiator_button_title_text: 'View details on the Stelrad website'
        }
    },
    
    output_correction_factors: [
        0,      //  0°C
        0.05,   //  5°C
        0.123,  // 10°C
        0.209,  // 15°C
        0.304,  // 20°C
        0.406,  // 25°C
        0.515,  // 30°C
        0.629,  // 35°C
        0.748,  // 40°C
        0.872,  // 45°C
        1,      // 50°C
        1.132,  // 55°C
        1.267,  // 60°C
        1.406,  // 65°C
        1.549,  // 70°C
        1.694   // 75°C
    ],
    
    default_room_height: 2.4,
    default_room_temperature: 21,
    default_air_changes: 0.5,
    
    standard_door_dims: 1.98 * 0.76, // m
    
    default_delta_t: 50, // Based on BS EN 442
    default_mwt: 70, // Based on BS EN 442 Flow Temp 75°C & Return Temp 65°C
    party_temperature: 10,
    external_temperature: -3,
    ventilation_factor: 0.33,
    
    heatloss_tolerance_factor: 1.1, // Multiplier applied to room heatloss to provide margin for radiator suggestions
    
    null_glazing_type: {
        name: 'No external windows or doors', 
        description: '',
        uvalue: 0
    },
    
    room_types: [
        { name: 'Bathroom', temperature: 22, air_changes: 0.5, img: 'bathroom.png' },
        { name: 'Bedroom', temperature: 21, air_changes: 0.5, img: 'bedroom.png' },
        { name: 'Bedroom/En Suite', temperature: 21, air_changes: 1.0, img: 'bedroom-ensuite.png' },
        { name: 'Dining Room', temperature: 21, air_changes: 0.5, img: 'dining-room.png' },
        { name: 'Hall', temperature: 21, air_changes: 0.5, img: 'hall.png' },
        { name: 'Kitchen', temperature: 21, air_changes: 0.5, img: 'kitchen.png' },
        { name: 'Kitchen/Diner', temperature: 21, air_changes: 0.5, img: 'kitchen-diner.png' },
        { name: 'Landing', temperature: 21, air_changes: 0.5, img: 'landing.png' },
        { name: 'Living Room', temperature: 21, air_changes: 0.5, img: 'living-room.png' },
        { name: 'Study', temperature: 21, air_changes: 0.5, img: 'study.png' },
        { name: 'Utility Room', temperature: 21, air_changes: 0.5, img: 'utility-room.png' },
        { name: 'WC', temperature: 21, air_changes: 1.5, img: 'wc.png' }
    ],
    
    internal_wall_types: [
        {
            name: 'Internal brick wall', // STARS ID 11
            description: 'Brick 105mm, Air Gap 25mm, Brick 105mm (+ 13mm Plaster)', // STARS ID 11
            uvalue: 1.50
        }
    ],
    
    external_wall_types: [
        {
            name: '220mm solid brick plastered', // STARS ID 469
            description: '220mm brickwork, 13mm dense plaster',
            uvalue: 2.10
        },
        {
            name: '105mm solid brick plastered', // STARS ID 468
            description: '105mm brickwork, 13mm dense plaster',
            uvalue: 3.00
        },
        {
            name: 'Brick cavity wall', // STARS ID 416
            description: '105mm brick, 50mm air space, 105mm brick, 13mm dense plaster',
            uvalue: 1.47
        },
        {
            name: 'Insulated brick cavity wall', // STARS ID 530
            description: '105mm brickwork, 50mm mineral fibre, 105mm brickwork, 13mm lightweight plaster',
            uvalue: 0.50
        },
        {
            name: 'Timber frame wall', // STARS ID 435
            description: '105mm brick, 50mm airspace, 19mm plywood sheeting, 95mm studding, 95mm mineral fibre insulation between studs, 13mm plaster board',
            uvalue: 0.29
        }
    ],
    
    glazing_types: [
        {
            name: 'Wood/PVCu Single Glazed',
            description: 'Wooden/PVCu Frame Single Glazed', // STARS ID 2
            uvalue: 5.0
        },
        {
            name: 'Wood/PVCu Double Glazed',
            description: 'Wooden/PVCu Frame Double Glazed', // STARS ID 3
            uvalue: 2.9
        },
        {
            name: 'Low E Double Glazed',
            description: 'Wooden/PVCu Frame Double Glazed, 20mm Gap Low E', // STARS ID 4
            uvalue: 1.7
        },
        {
            name: 'Metal Frame Single Glazed', 
            description: 'Metal Frame Single Glazed', // STARS ID 10
            uvalue: 5.8
        }
    ],
    
    internal_doors: [
        {
            name: 'Timber Panel', // STARS ID 5
            uvalue: 2.2
        }
    ],
    
    floor_types: [
        {
            name: 'Heated room', // STARS ID 30
            description: 'Timber Joists, Boarding 19mm, Plasterboard 13mm',
            uvalue: 1.36,
            adjacent_temperature: 21
        },
        {
            name: 'Timber floor over ventilated air gap', // STARS ID 28
            description: 'Bare Boards on Joists, Airbrick 2+ Sides',
            uvalue: 0.82
        },
        {
            name: 'Uninsulated solid floor on earth', // STARS ID 37
            description: 'Solid on Earth - no insulation',
            uvalue: 0.82
        },
        {
            name: 'Solid concrete floor', // STARS ID 40
            description: 'Sand cement rendered ground floor',
            uvalue: 1.6
        }
    ],
    
    ceiling_types: [
        {
            name: 'Heated room', // STARS ID 32
            description: 'Timber Joists, Boarding 19mm, Plasterboard 13mm', 
            uvalue: 1.62,
            adjacent_temperature: 18
        },
        {
            name: 'Insulated roof space', // STARS ID 95
            description: '12.5 mm plasterboard, 50 mm mineral wool quilt between ceiling Joists, roof space, tiling',
            uvalue: 0.71
        },
        {
            name: 'Uninsulated roof space', // STARS ID 93
            description: '12.5 mm plasterboard, no insulation, roof space, tiling',
            uvalue: 2.30
        },
        {
            name: 'Insulated flat roof', // STARS ID 27
            description: 'Flat Roof - Felt, 50mm Insulation',
            uvalue: 0.70
        },
        {
            name: 'Uninsulated flat roof', // STARS ID 82
            description: 'Flat Concrete - Waterproof covering, 75 mm screed, 150 mm cast concrete. 13 mm dense plaster',
            uvalue: 2.19
        }
    ]
};
